/**
 * Settings page — admin only.
 * Manages billing rates, aircraft details, pilot admin, currency thresholds, airports.
 * Task 11.1: Billing rates section with inline editing.
 *
 * Requirements: 7.1, 7.2, 7.3, 7.4
 */

import { createDataGrid } from '../components/data-grid.js';
import {
  getBillingRates,
  updateBillingRate,
  createBillingRate,
  deleteBillingRate,
  getAircraft,
  updateAircraftDetails,
  getAircraftAirports,
  upsertAircraftAirports,
  getPilots,
  getPilotLicences,
  createPilotLicence,
  deletePilotLicence,
  getLicenceAircraftMappings,
  upsertLicenceAircraftMapping,
  deleteLicenceAircraftMapping,
  getPilotFlyingHours,
  getFlights,
  getCurrencyThresholds,
  upsertCurrencyThreshold,
  deleteCurrencyThreshold,
  getAirports,
  upsertAirport,
  deleteAirport,
  getCharges,
  getChargeTypes,
  upsertCharge,
  deleteCharge,
} from '../services/api.js';
import { isAdmin } from '../services/auth.js';
import { navigate } from '../services/router.js';
import { formatGBP, formatDateUK } from '../utils/format.js';
import { validateICAOCode, validateCurrencyThresholdPayload } from '../utils/validation.js';
import { LICENCE_TYPES, RATING_CATEGORIES } from '../utils/constants.js';
import { openDetailModal } from '../components/detail-modal.js';
import { createPilotManagement } from '../components/settings/pilot-management.js';
import { createAircraftManagement } from '../components/settings/aircraft-management.js';
import { createAirportReference } from '../components/settings/airport-reference.js';
import { createDatabaseTab } from '../components/settings/database-tab.js';

export async function renderSettings() {
  const app = document.getElementById('app');
  if (!app) return;

  if (!isAdmin()) {
    navigate('/');
    return;
  }

  app.innerHTML = `
    <div class="container">
      <h2>Settings</h2>
      <div id="settings-msg"></div>
      <div class="settings-tabs">
        <nav class="settings-tabs__nav" role="tablist">
          <button class="settings-tabs__tab settings-tabs__tab--active" role="tab" aria-selected="true" data-tab="users">User Manager</button>
          <button class="settings-tabs__tab" role="tab" aria-selected="false" data-tab="airports">Airports &amp; Airstrips</button>
          <button class="settings-tabs__tab" role="tab" aria-selected="false" data-tab="aircraft">Aircraft Management</button>
          <button class="settings-tabs__tab" role="tab" aria-selected="false" data-tab="billing-rates">Billing Rates</button>
          <button class="settings-tabs__tab" role="tab" aria-selected="false" data-tab="currency">Currency &amp; Costs</button>
          <button class="settings-tabs__tab" role="tab" aria-selected="false" data-tab="database">Database</button>
        </nav>
        <div class="settings-tabs__content">
          <div class="settings-tabs__panel settings-tabs__panel--active card" id="tab-users" role="tabpanel">
            <div id="pilot-management-section"></div>
          </div>
          <div class="settings-tabs__panel card" id="tab-airports" role="tabpanel">
            <div id="airport-reference-section"></div>
          </div>
          <div class="settings-tabs__panel card" id="tab-aircraft" role="tabpanel">
            <div id="aircraft-management-section"></div>
          </div>
          <div class="settings-tabs__panel card" id="tab-billing-rates" role="tabpanel">
            <div id="billing-rates-section">Loading…</div>
          </div>
          <div class="settings-tabs__panel card" id="tab-currency" role="tabpanel">
            <h3>Currency Thresholds</h3>
            <div id="currency-thresholds-section">Loading…</div>
            <h3 style="margin-top:2rem">Charges</h3>
            <p class="text-muted" style="margin-bottom:1rem">Fuel, oil and landing fee rates by period (YYYY, YYYYmNN, YYYYwNN or range e.g. 2025m01/2025m06).</p>
            <div id="charges-section">Loading…</div>
          </div>
          <div class="settings-tabs__panel card" id="tab-database" role="tabpanel">
            <div id="database-tab-section"></div>
          </div>
        </div>
      </div>
    </div>
  `;

  // Tab switching
  document.querySelector('.settings-tabs__nav')?.addEventListener('click', (e) => {
    const btn = e.target.closest('.settings-tabs__tab');
    if (!btn) return;
    document.querySelectorAll('.settings-tabs__tab').forEach(t => {
      t.classList.remove('settings-tabs__tab--active');
      t.setAttribute('aria-selected', 'false');
    });
    document.querySelectorAll('.settings-tabs__panel').forEach(p => p.classList.remove('settings-tabs__panel--active'));
    btn.classList.add('settings-tabs__tab--active');
    btn.setAttribute('aria-selected', 'true');
    document.getElementById(`tab-${btn.dataset.tab}`)?.classList.add('settings-tabs__panel--active');
  });

  // Wire new component sections
  const pilotMgmtContainer = document.getElementById('pilot-management-section');
  if (pilotMgmtContainer) {
    pilotMgmtContainer.appendChild(createPilotManagement());
  }

  const aircraftMgmtContainer = document.getElementById('aircraft-management-section');
  if (aircraftMgmtContainer) {
    aircraftMgmtContainer.appendChild(createAircraftManagement());
  }

  const airportRefContainer = document.getElementById('airport-reference-section');
  if (airportRefContainer) {
    airportRefContainer.appendChild(createAirportReference());
  }

  const databaseTabContainer = document.getElementById('database-tab-section');
  if (databaseTabContainer) {
    databaseTabContainer.appendChild(createDatabaseTab());
  }

  await Promise.all([loadBillingRates(), loadCurrencyThresholds(), loadCharges()]);
}

/**
 * Load and render the billing rates data grid with inline editing.
 */
async function loadBillingRates() {
  const container = document.getElementById('billing-rates-section');
  if (!container) return;

  try {
    const rates = await getBillingRates();
    renderBillingRatesGrid(container, rates);
  } catch (err) {
    container.innerHTML = `<p class="error">Failed to load billing rates: ${err.message}</p>`;
  }
}

/**
 * Render the billing rates grid with Add and per-row Edit buttons.
 * Uses createDataGrid and openDetailModal for editing and deletion.
 */
function renderBillingRatesGrid(container, rates) {
  container.innerHTML = '';

  /** @type {Object[]} */
  let allAircraft = [];

  // Load aircraft list for the add/edit form
  getAircraft().then((ac) => { allAircraft = ac; }).catch(() => {});

  const grid = createDataGrid({
    columns: [
      {
        key: 'aircraft_registration',
        label: 'Aircraft',
        format: (_val, row) => row.aircraft?.registration || '—',
      },
      {
        key: 'rate_per_hour',
        label: 'Rate / Hour',
        align: 'right',
        format: (val) => formatGBP(val),
      },
      {
        key: 'billing_basis',
        label: 'Billing Basis',
        format: (val) => val === 'FLIGHT_TIME' ? 'Flight Time' : 'Block Time',
      },
      {
        key: 'effective_from',
        label: 'Effective From',
        format: (val) => val ? formatDateUK(val) : '—',
      },
    ],
    data: rates,
    defaultSort: 'aircraft_registration',
    defaultOrder: 'asc',
    emptyMessage: 'No billing rates configured.',
    filterable: false,
    heading: 'Billing Rates',
    addLabel: 'Add Billing Rate',
    onAdd: () => openBillingRateModal(null, allAircraft),
    onRowClick: (row) => openBillingRateModal(row, allAircraft),
  });

  container.appendChild(grid);
}

/**
 * Open the detail modal for adding or editing a billing rate.
 * @param {Object|null} rate - Existing rate for edit, null for add
 * @param {Object[]} aircraftList - Available aircraft
 */
function openBillingRateModal(rate, aircraftList) {
  const isEdit = !!rate;
  const identifier = rate?.aircraft?.registration || '';
  const title = isEdit ? `Edit Billing Rate — ${identifier}` : 'Add Billing Rate';

  const aircraftOptions = aircraftList.map((a) => ({
    value: a.id,
    label: a.registration,
  }));

  openDetailModal({
    title,
    fields: [
      {
        name: 'aircraft_id',
        label: 'Aircraft',
        type: 'select',
        required: true,
        options: aircraftOptions,
      },
      { name: 'rate_per_hour', label: 'Rate per Hour', type: 'number', required: true },
      {
        name: 'billing_basis',
        label: 'Billing Basis',
        type: 'select',
        required: true,
        options: [
          { value: 'BLOCK_TIME', label: 'Block Time' },
          { value: 'FLIGHT_TIME', label: 'Flight Time' },
        ],
      },
      { name: 'effective_from', label: 'Effective From', type: 'date', required: true },
    ],
    values: isEdit
      ? {
          aircraft_id: rate.aircraft_id,
          rate_per_hour: rate.rate_per_hour,
          billing_basis: rate.billing_basis || 'BLOCK_TIME',
          effective_from: rate.effective_from || '',
        }
      : { billing_basis: 'BLOCK_TIME' },
    onDelete: isEdit
      ? async () => {
          const identifier = rate.aircraft?.registration || 'this billing rate';
          if (!window.confirm(`Are you sure you want to delete billing rate for ${identifier}?`)) {
            throw new Error('Cancelled');
          }
          try {
            await deleteBillingRate(rate.id);
            showSettingsMsg('Billing rate deleted successfully.', false);
            await loadBillingRates();
          } catch (err) {
            if (isForeignKeyViolation(err)) {
              throw new Error(`Cannot delete billing rate for ${identifier}: it is referenced by billing records.`);
            }
            throw err;
          }
        }
      : undefined,
    onSave: async (formData) => {
      const newRate = parseFloat(formData.rate_per_hour);
      if (isNaN(newRate) || newRate < 0) {
        const err = new Error('Rate per hour must be a valid positive number.');
        err.fieldErrors = { rate_per_hour: 'Rate must be a positive number' };
        throw err;
      }

      const payload = {
        aircraft_id: formData.aircraft_id,
        rate_per_hour: newRate,
        billing_basis: formData.billing_basis,
        effective_from: formData.effective_from,
        updated_at: new Date().toISOString(),
      };

      if (isEdit) {
        await updateBillingRate(rate.id, payload);
      } else {
        await createBillingRate(payload);
      }

      showSettingsMsg(isEdit ? 'Billing rate updated successfully.' : 'Billing rate added successfully.', false);
      await loadBillingRates();
    },
  });
}

/**
 * Display a message in the settings page message area.
 */
function showSettingsMsg(text, isError) {
  const el = document.getElementById('settings-msg');
  if (!el) return;
  el.textContent = text;
  el.className = isError ? 'error' : 'success';
  setTimeout(() => {
    el.textContent = '';
    el.className = '';
  }, 4000);
}

/**
 * Check if an error is a Postgres foreign key violation (23503).
 * @param {Error} err
 * @returns {boolean}
 */
function isForeignKeyViolation(err) {
  const msg = (err.message || '') + (err.code || '');
  return msg.includes('23503') || msg.toLowerCase().includes('foreign key');
}

/**
 * Confirm deletion with a standard prompt.
 * @param {string} identifier - Human-readable identifier for the item
 * @returns {boolean}
 */
function confirmDelete(identifier) {
  return window.confirm(`Are you sure you want to delete ${identifier}?`);
}


// ── Aircraft Details Section (Task 11.2) ──────────────

/**
 * Load and render the aircraft details data grid.
 */
async function loadAircraftDetails() {
  const container = document.getElementById('aircraft-details-section');
  if (!container) return;

  try {
    const aircraft = await getAircraft();
    renderAircraftDetailsGrid(container, aircraft);
  } catch (err) {
    container.innerHTML = `<p class="error">Failed to load aircraft: ${err.message}</p>`;
  }
}

/**
 * Render the aircraft details grid with expandable edit panels.
 * Shows registration, type, status, total hours in a Data_Grid.
 * Each row can be expanded to edit max_oil_capacity, fuel_tank_config, and airport codes.
 */
function renderAircraftDetailsGrid(container, aircraft) {
  container.innerHTML = '';

  // Summary grid
  const grid = createDataGrid({
    columns: [
      { key: 'registration', label: 'Registration' },
      { key: 'type', label: 'Type' },
      { key: 'status', label: 'Status' },
      { key: 'total_hours', label: 'Total Hours', format: (v) => v != null ? Number(v).toFixed(1) : '—' },
    ],
    data: aircraft,
    defaultSort: 'registration',
    defaultOrder: 'asc',
    emptyMessage: 'No aircraft found.',
    filterable: false,
  });
  container.appendChild(grid);

  // Make rows clickable to expand edit panel
  const rows = grid.querySelectorAll('.data-grid__row');
  rows.forEach((row, idx) => {
    row.style.cursor = 'pointer';
    row.addEventListener('click', () => {
      toggleAircraftEditPanel(container, aircraft[idx], row);
    });
  });
}

/**
 * Toggle the edit panel for an aircraft row.
 * If already open, close it. Otherwise, close any other open panel and open this one.
 */
function toggleAircraftEditPanel(container, ac, row) {
  const existingPanel = container.querySelector(`.aircraft-edit-panel[data-aircraft-id="${ac.id}"]`);
  if (existingPanel) {
    existingPanel.remove();
    return;
  }

  // Close any other open panels
  container.querySelectorAll('.aircraft-edit-panel').forEach((p) => p.remove());

  const panel = document.createElement('div');
  panel.className = 'aircraft-edit-panel';
  panel.dataset.aircraftId = ac.id;
  panel.style.cssText = 'padding:1rem;border:1px solid #ddd;border-radius:4px;margin:0.5rem 0 1rem;background:#fafafa;';

  const tanks = parseTanks(ac.fuel_tank_config);
  const unit = ac.oil_measurement_unit || 'quarts';

  panel.innerHTML = `
    <h4 style="margin-top:0;">${ac.registration} — Edit Details</h4>
    <div class="aircraft-edit-fields" style="display:grid;grid-template-columns:1fr 1fr;gap:1rem;">
      <div>
        <label for="oil-cap-${ac.id}">Max Oil Capacity (${unit})</label>
        <input type="number" id="oil-cap-${ac.id}" step="0.01" min="0"
               value="${ac.max_oil_capacity != null ? ac.max_oil_capacity : ''}"
               class="oil-cap-input" style="width:100%;padding:0.4rem;" />
      </div>
      <div>
        <label for="tanks-${ac.id}">Fuel Tanks (comma-separated)</label>
        <input type="text" id="tanks-${ac.id}"
               value="${tanks.join(', ')}"
               class="tanks-input" style="width:100%;padding:0.4rem;" />
      </div>
    </div>
    <div style="margin-top:1rem;">
      <label>Airport Codes (ICAO)</label>
      <div id="airport-codes-${ac.id}" class="airport-codes-list"></div>
      <div style="margin-top:0.5rem;display:flex;gap:0.5rem;align-items:flex-start;">
        <div>
          <input type="text" id="new-code-${ac.id}" placeholder="e.g. EGLL"
                 class="new-code-input" maxlength="4" style="padding:0.4rem;width:100px;text-transform:uppercase;" />
          <span id="code-error-${ac.id}" class="field-error" style="display:none;color:red;font-size:0.85rem;"></span>
        </div>
        <button class="btn btn--sm add-code-btn" data-aircraft-id="${ac.id}">Add</button>
      </div>
    </div>
    <div style="margin-top:1rem;">
      <button class="btn btn--primary btn--sm save-aircraft-btn" data-aircraft-id="${ac.id}">Save</button>
      <button class="btn btn--sm close-panel-btn" data-aircraft-id="${ac.id}" style="margin-left:0.5rem;">Cancel</button>
    </div>
  `;

  // Insert panel after the clicked row's parent table
  row.closest('table').insertAdjacentElement('afterend', panel);

  // Load airport codes for this aircraft
  loadAirportCodes(ac.id);

  // Attach panel listeners
  attachAircraftPanelListeners(panel, ac);
}

/**
 * Parse fuel_tank_config JSONB into an array of tank names.
 */
function parseTanks(config) {
  if (!config) return [];
  if (typeof config === 'string') {
    try { config = JSON.parse(config); } catch { return []; }
  }
  if (Array.isArray(config)) return config;
  if (config.tanks && Array.isArray(config.tanks)) return config.tanks;
  return [];
}

/** In-memory store for airport codes per aircraft while editing. */
const aircraftAirportCodes = {};

/**
 * Load airport codes for an aircraft and render them as removable tags.
 */
async function loadAirportCodes(aircraftId) {
  const listEl = document.getElementById(`airport-codes-${aircraftId}`);
  if (!listEl) return;

  try {
    const entries = await getAircraftAirports(aircraftId);
    aircraftAirportCodes[aircraftId] = entries.map((e) => e.icao_code);
  } catch {
    aircraftAirportCodes[aircraftId] = [];
  }

  renderAirportCodeTags(aircraftId);
}

/**
 * Render airport code tags with remove buttons.
 */
function renderAirportCodeTags(aircraftId) {
  const listEl = document.getElementById(`airport-codes-${aircraftId}`);
  if (!listEl) return;

  const codes = aircraftAirportCodes[aircraftId] || [];
  if (codes.length === 0) {
    listEl.innerHTML = '<span style="color:#888;font-size:0.9rem;">No airport codes configured</span>';
    return;
  }

  listEl.innerHTML = codes
    .map(
      (code) =>
        `<span class="airport-code-tag" style="display:inline-flex;align-items:center;gap:0.25rem;background:#e8f0fe;padding:0.2rem 0.5rem;border-radius:3px;margin:0.2rem;font-size:0.9rem;">
          ${code}
          <button class="remove-code-btn" data-aircraft-id="${aircraftId}" data-code="${code}"
                  style="border:none;background:none;cursor:pointer;color:#c00;font-weight:bold;padding:0 2px;">&times;</button>
        </span>`
    )
    .join('');

  // Attach remove listeners
  listEl.querySelectorAll('.remove-code-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const code = btn.dataset.code;
      const id = btn.dataset.aircraftId;
      aircraftAirportCodes[id] = (aircraftAirportCodes[id] || []).filter((c) => c !== code);
      renderAirportCodeTags(id);
    });
  });
}

/**
 * Attach event listeners for the aircraft edit panel (add code, save, cancel).
 */
function attachAircraftPanelListeners(panel, ac) {
  // Add airport code
  const addBtn = panel.querySelector('.add-code-btn');
  const newCodeInput = panel.querySelector(`#new-code-${ac.id}`);
  const codeError = panel.querySelector(`#code-error-${ac.id}`);

  addBtn.addEventListener('click', () => {
    const raw = newCodeInput.value.trim().toUpperCase();
    codeError.style.display = 'none';
    codeError.textContent = '';

    if (!raw) return;

    const result = validateICAOCode(raw);
    if (!result.valid) {
      codeError.textContent = result.errors[0];
      codeError.style.display = 'block';
      return;
    }

    const codes = aircraftAirportCodes[ac.id] || [];
    if (codes.includes(raw)) {
      codeError.textContent = 'Code already added';
      codeError.style.display = 'block';
      return;
    }

    aircraftAirportCodes[ac.id] = [...codes, raw];
    renderAirportCodeTags(ac.id);
    newCodeInput.value = '';
  });

  // Close panel
  panel.querySelector('.close-panel-btn').addEventListener('click', () => {
    panel.remove();
  });

  // Save
  panel.querySelector('.save-aircraft-btn').addEventListener('click', async () => {
    const saveBtn = panel.querySelector('.save-aircraft-btn');
    const oilInput = panel.querySelector(`#oil-cap-${ac.id}`);
    const tanksInput = panel.querySelector(`#tanks-${ac.id}`);

    // Validate all airport codes before saving
    const codes = aircraftAirportCodes[ac.id] || [];
    for (const code of codes) {
      const result = validateICAOCode(code);
      if (!result.valid) {
        codeError.textContent = `Invalid code "${code}": ${result.errors[0]}`;
        codeError.style.display = 'block';
        return;
      }
    }

    const oilVal = oilInput.value.trim();
    const maxOil = oilVal === '' ? null : parseFloat(oilVal);
    if (oilVal !== '' && (isNaN(maxOil) || maxOil < 0)) {
      showSettingsMsg('Max oil capacity must be a valid positive number.', true);
      return;
    }

    const tankNames = tanksInput.value
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    saveBtn.disabled = true;
    saveBtn.textContent = 'Saving…';

    try {
      // Update aircraft details (max_oil_capacity, fuel_tank_config)
      await updateAircraftDetails(ac.id, {
        max_oil_capacity: maxOil,
        fuel_tank_config: { tanks: tankNames },
        updated_at: new Date().toISOString(),
      });

      // Persist airport codes
      await upsertAircraftAirports(ac.id, codes);

      showSettingsMsg(`Aircraft ${ac.registration} updated successfully.`, false);
      panel.remove();
      await loadAircraftDetails();
    } catch (err) {
      showSettingsMsg(`Failed to update aircraft: ${err.message}`, true);
      saveBtn.disabled = false;
      saveBtn.textContent = 'Save';
    }
  });
}


// ── Pilot Administration Section (Task 11.3) ──────────

/** In-memory cache for pilot data used during rendering. */
let pilotAdminData = { pilots: [], licences: {}, mappings: [], hours: {}, aircraft: [], flightsByPilot: {} };

/**
 * Load all pilot admin data and render the section.
 */
async function loadPilotAdmin() {
  const container = document.getElementById('pilot-admin-section');
  if (!container) return;

  try {
    const [pilots, mappings, aircraft] = await Promise.all([
      getPilots(),
      getLicenceAircraftMappings(),
      getAircraft(),
    ]);

    // Fetch licences and flying hours for each pilot in parallel
    const licenceResults = await Promise.all(pilots.map((p) => getPilotLicences(p.id)));
    const hoursResults = await Promise.all(pilots.map((p) => getPilotFlyingHours(p.id)));
    // Fetch flights per pilot for per-aircraft-type breakdown
    const flightResults = await Promise.all(pilots.map((p) => getFlights({ pilot_id: p.id })));

    const licences = {};
    const hours = {};
    const flightsByPilot = {};
    pilots.forEach((p, i) => {
      licences[p.id] = licenceResults[i] || [];
      hours[p.id] = hoursResults[i] || { total_minutes: 0, last_90_days_minutes: 0 };
      flightsByPilot[p.id] = flightResults[i] || [];
    });

    pilotAdminData = { pilots, licences, mappings, hours, aircraft, flightsByPilot };
    renderPilotAdmin(container);
  } catch (err) {
    container.innerHTML = `<p class="error">Failed to load pilot data: ${err.message}</p>`;
  }
}

/**
 * Render the pilot admin section: pilot list grid + expandable licence panels + mappings.
 */
function renderPilotAdmin(container) {
  const { pilots, hours } = pilotAdminData;
  container.innerHTML = '';

  if (pilots.length === 0) {
    container.innerHTML = '<p>No pilots found.</p>';
    return;
  }

  // Pilot summary grid
  const grid = createDataGrid({
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'email', label: 'Email' },
      {
        key: 'total_hours',
        label: 'Total Hours',
        format: (_v, row) => {
          const h = hours[row.id];
          return h ? (h.total_minutes / 60).toFixed(1) : '0.0';
        },
      },
      {
        key: 'last_90_days_hours',
        label: '90-Day Hours',
        format: (_v, row) => {
          const h = hours[row.id];
          return h ? (h.last_90_days_minutes / 60).toFixed(1) : '0.0';
        },
      },
    ],
    data: pilots,
    defaultSort: 'name',
    defaultOrder: 'asc',
    emptyMessage: 'No pilots found.',
    filterable: true,
  });
  container.appendChild(grid);

  // Make rows expandable
  const rows = grid.querySelectorAll('.data-grid__row');
  rows.forEach((row, idx) => {
    row.style.cursor = 'pointer';
    row.addEventListener('click', () => {
      togglePilotPanel(container, pilots[idx]);
    });
  });

  // Licence-Aircraft Mappings section
  const mappingsSection = document.createElement('div');
  mappingsSection.style.cssText = 'margin-top:1.5rem;';
  mappingsSection.innerHTML = '<h4>Licence-Aircraft Mappings</h4>';
  const mappingsContainer = document.createElement('div');
  mappingsContainer.id = 'mappings-section';
  mappingsSection.appendChild(mappingsContainer);
  container.appendChild(mappingsSection);
  renderMappingsSection(mappingsContainer);
}

/**
 * Toggle the expanded panel for a pilot showing licences and per-type hours.
 */
function togglePilotPanel(container, pilot) {
  const existingPanel = container.querySelector(`.pilot-panel[data-pilot-id="${pilot.id}"]`);
  if (existingPanel) {
    existingPanel.remove();
    return;
  }

  // Close any other open pilot panels
  container.querySelectorAll('.pilot-panel').forEach((p) => p.remove());

  const panel = document.createElement('div');
  panel.className = 'pilot-panel';
  panel.dataset.pilotId = pilot.id;
  panel.style.cssText = 'padding:1rem;border:1px solid #ddd;border-radius:4px;margin:0.5rem 0 1rem;background:#fafafa;';

  const licences = pilotAdminData.licences[pilot.id] || [];
  const flights = pilotAdminData.flightsByPilot[pilot.id] || [];

  // Compute per-aircraft-type hours
  const perTypeHours = computePerTypeHours(flights);

  panel.innerHTML = `
    <h4 style="margin-top:0;">${pilot.name || pilot.email} — Licences &amp; Hours</h4>

    <div class="pilot-type-hours" style="margin-bottom:1rem;">
      <strong>Hours by Aircraft Type:</strong>
      ${perTypeHours.length > 0
        ? `<table class="data-grid__table" style="margin-top:0.5rem;">
            <thead><tr>
              <th class="data-grid__header">Aircraft Type</th>
              <th class="data-grid__header">Total Hours</th>
              <th class="data-grid__header">90-Day Hours</th>
            </tr></thead>
            <tbody>
              ${perTypeHours.map((t) => `
                <tr class="data-grid__row">
                  <td class="data-grid__cell">${t.type}</td>
                  <td class="data-grid__cell">${t.totalHours}</td>
                  <td class="data-grid__cell">${t.last90Hours}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>`
        : '<p style="color:#888;font-size:0.9rem;">No flights logged.</p>'}
    </div>

    <div class="pilot-licences-list">
      <strong>Licences:</strong>
      ${licences.length > 0
        ? `<table class="data-grid__table" style="margin-top:0.5rem;">
            <thead><tr>
              <th class="data-grid__header">Type</th>
              <th class="data-grid__header">Rating</th>
              <th class="data-grid__header">Expiry</th>
              <th class="data-grid__header"></th>
            </tr></thead>
            <tbody>
              ${licences.map((l) => `
                <tr class="data-grid__row">
                  <td class="data-grid__cell">${l.licence_type}</td>
                  <td class="data-grid__cell">${l.rating_category}</td>
                  <td class="data-grid__cell">${formatDateUK(l.expiry_date)}</td>
                  <td class="data-grid__cell">
                    <button class="btn btn--sm btn--danger delete-licence-btn" data-licence-id="${l.id}">Delete</button>
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>`
        : '<p style="color:#888;font-size:0.9rem;">No licences assigned.</p>'}
    </div>

    <div class="add-licence-form" style="margin-top:1rem;padding:0.75rem;border:1px solid #e0e0e0;border-radius:4px;background:#fff;">
      <strong>Add Licence</strong>
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:0.75rem;margin-top:0.5rem;">
        <div>
          <label for="licence-type-${pilot.id}">Licence Type</label>
          <select id="licence-type-${pilot.id}" class="licence-type-select" style="width:100%;padding:0.4rem;">
            ${LICENCE_TYPES.map((t) => `<option value="${t}">${t}</option>`).join('')}
          </select>
        </div>
        <div>
          <label for="rating-cat-${pilot.id}">Rating Category</label>
          <select id="rating-cat-${pilot.id}" class="rating-cat-select" style="width:100%;padding:0.4rem;">
            ${RATING_CATEGORIES.map((r) => `<option value="${r}">${r}</option>`).join('')}
          </select>
        </div>
        <div>
          <label for="expiry-date-${pilot.id}">Expiry Date</label>
          <input type="date" id="expiry-date-${pilot.id}" class="expiry-date-input" style="width:100%;padding:0.4rem;" />
        </div>
      </div>
      <button class="btn btn--primary btn--sm add-licence-btn" data-pilot-id="${pilot.id}" style="margin-top:0.75rem;">Add Licence</button>
    </div>
  `;

  // Insert after the grid table
  const table = container.querySelector('.data-grid__table');
  if (table) {
    table.insertAdjacentElement('afterend', panel);
  } else {
    container.appendChild(panel);
  }

  attachPilotPanelListeners(panel, pilot);
}

/**
 * Compute per-aircraft-type flying hours from a pilot's flights.
 * Returns array of { type, totalHours, last90Hours }.
 */
function computePerTypeHours(flights) {
  const now = new Date();
  const ninetyDaysAgo = new Date(now);
  ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
  const cutoff = ninetyDaysAgo.toISOString().split('T')[0];

  const byType = {};
  for (const f of flights) {
    const type = f.aircraft?.type || 'Unknown';
    if (!byType[type]) byType[type] = { totalMins: 0, last90Mins: 0 };
    byType[type].totalMins += f.flight_time_mins || 0;
    if (f.flight_date >= cutoff) {
      byType[type].last90Mins += f.flight_time_mins || 0;
    }
  }

  return Object.entries(byType).map(([type, data]) => ({
    type,
    totalHours: (data.totalMins / 60).toFixed(1),
    last90Hours: (data.last90Mins / 60).toFixed(1),
  }));
}

/**
 * Attach event listeners for the pilot panel (add licence, delete licence).
 */
function attachPilotPanelListeners(panel, pilot) {
  // Delete licence buttons
  panel.querySelectorAll('.delete-licence-btn').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const licenceId = btn.dataset.licenceId;
      btn.disabled = true;
      btn.textContent = 'Deleting…';

      try {
        await deletePilotLicence(licenceId);
        showSettingsMsg('Licence deleted.', false);
        // Refresh pilot admin data
        pilotAdminData.licences[pilot.id] = await getPilotLicences(pilot.id);
        // Re-render the panel
        panel.remove();
        const container = document.getElementById('pilot-admin-section');
        if (container) togglePilotPanel(container, pilot);
      } catch (err) {
        showSettingsMsg(`Failed to delete licence: ${err.message}`, true);
        btn.disabled = false;
        btn.textContent = 'Delete';
      }
    });
  });

  // Add licence button
  const addBtn = panel.querySelector('.add-licence-btn');
  addBtn.addEventListener('click', async () => {
    const typeSelect = panel.querySelector(`#licence-type-${pilot.id}`);
    const ratingSelect = panel.querySelector(`#rating-cat-${pilot.id}`);
    const expiryInput = panel.querySelector(`#expiry-date-${pilot.id}`);

    const licenceType = typeSelect.value;
    const ratingCategory = ratingSelect.value;
    const expiryDate = expiryInput.value;

    if (!expiryDate) {
      showSettingsMsg('Expiry date is required.', true);
      return;
    }

    addBtn.disabled = true;
    addBtn.textContent = 'Adding…';

    try {
      await createPilotLicence({
        pilot_id: pilot.id,
        licence_type: licenceType,
        rating_category: ratingCategory,
        expiry_date: expiryDate,
      });

      showSettingsMsg('Licence added successfully.', false);
      // Refresh pilot admin data
      pilotAdminData.licences[pilot.id] = await getPilotLicences(pilot.id);
      // Re-render the panel
      panel.remove();
      const container = document.getElementById('pilot-admin-section');
      if (container) togglePilotPanel(container, pilot);
    } catch (err) {
      showSettingsMsg(`Failed to add licence: ${err.message}`, true);
      addBtn.disabled = false;
      addBtn.textContent = 'Add Licence';
    }
  });
}

/**
 * Render the licence-aircraft mappings section with Add, per-row Edit and Delete buttons.
 * Uses createDataGrid with actions config and openDetailModal for editing.
 */
function renderMappingsSection(container) {
  const { mappings, aircraft } = pilotAdminData;
  container.innerHTML = '';

  // Get unique aircraft types
  const aircraftTypes = [...new Set(aircraft.map((a) => a.type).filter(Boolean))].sort();

  const grid = createDataGrid({
    columns: [
      { key: 'rating_category', label: 'Rating Category' },
      { key: 'aircraft_type', label: 'Aircraft Type' },
    ],
    data: mappings,
    defaultSort: 'rating_category',
    defaultOrder: 'asc',
    emptyMessage: 'No mappings configured.',
    filterable: false,
    addLabel: 'Add Mapping',
    onAdd: () => openMappingModal(null, aircraftTypes),
    actions: [
      {
        label: 'Edit',
        className: 'data-grid__action-btn--edit',
        onClick: (row) => openMappingModal(row, aircraftTypes),
      },
      {
        label: 'Delete',
        className: 'data-grid__action-btn--delete',
        onClick: (row) => handleDeleteMapping(row),
      },
    ],
  });

  container.appendChild(grid);
}

/**
 * Open the detail modal for adding or editing a licence-aircraft mapping.
 * @param {Object|null} mapping - Existing mapping for edit, null for add
 * @param {string[]} aircraftTypes - Available aircraft types
 */
function openMappingModal(mapping, aircraftTypes) {
  const isEdit = !!mapping;
  const title = isEdit
    ? `Edit Mapping — ${mapping.rating_category} / ${mapping.aircraft_type}`
    : 'Add Mapping';

  const typeOptions = aircraftTypes.length > 0
    ? aircraftTypes.map((t) => ({ value: t, label: t }))
    : [];

  openDetailModal({
    title,
    fields: [
      {
        name: 'rating_category',
        label: 'Rating Category',
        type: 'select',
        required: true,
        options: RATING_CATEGORIES.map((r) => ({ value: r, label: r })),
      },
      {
        name: 'aircraft_type',
        label: 'Aircraft Type',
        type: typeOptions.length > 0 ? 'select' : 'text',
        required: true,
        ...(typeOptions.length > 0 ? { options: typeOptions } : {}),
      },
    ],
    values: isEdit
      ? {
          rating_category: mapping.rating_category,
          aircraft_type: mapping.aircraft_type,
        }
      : {},
    onSave: async (formData) => {
      const ratingCategory = formData.rating_category;
      const aircraftType = formData.aircraft_type?.trim();

      if (!aircraftType) {
        const err = new Error('Aircraft type is required.');
        err.fieldErrors = { aircraft_type: 'Aircraft type is required' };
        throw err;
      }

      await upsertLicenceAircraftMapping({
        rating_category: ratingCategory,
        aircraft_type: aircraftType,
      });

      showSettingsMsg(
        isEdit ? 'Mapping updated successfully.' : 'Mapping added successfully.',
        false,
      );
      pilotAdminData.mappings = await getLicenceAircraftMappings();
      const mappingsContainer = document.getElementById('mappings-section');
      if (mappingsContainer) renderMappingsSection(mappingsContainer);
    },
  });
}

/**
 * Handle deletion of a licence-aircraft mapping with confirmation and FK error handling.
 * @param {Object} mapping
 */
async function handleDeleteMapping(mapping) {
  const identifier = `mapping ${mapping.rating_category} / ${mapping.aircraft_type}`;
  if (!confirmDelete(identifier)) return;

  try {
    await deleteLicenceAircraftMapping(mapping.id);
    showSettingsMsg('Mapping deleted successfully.', false);
    pilotAdminData.mappings = await getLicenceAircraftMappings();
    const mappingsContainer = document.getElementById('mappings-section');
    if (mappingsContainer) renderMappingsSection(mappingsContainer);
  } catch (err) {
    if (isForeignKeyViolation(err)) {
      showSettingsMsg(`Cannot delete ${identifier}: it is referenced by other records.`, true);
    } else {
      showSettingsMsg(`Failed to delete mapping: ${err.message}`, true);
    }
  }
}

// ── Currency Thresholds Section (Task 11.4) ───────────

/** In-memory cache for currency thresholds data. */
let currencyThresholdsData = { thresholds: [], aircraftTypes: [] };

/**
 * Load currency thresholds and render the section.
 */
async function loadCurrencyThresholds() {
  const container = document.getElementById('currency-thresholds-section');
  if (!container) return;

  try {
    const [thresholds, aircraft] = await Promise.all([
      getCurrencyThresholds(),
      getAircraft(),
    ]);

    const aircraftTypes = [...new Set(aircraft.map((a) => a.type).filter(Boolean))].sort();
    currencyThresholdsData = { thresholds, aircraftTypes };
    renderCurrencyThresholds(container);
  } catch (err) {
    container.innerHTML = `<p class="error">Failed to load currency thresholds: ${err.message}</p>`;
  }
}

/**
 * Render the currency thresholds grid with Add, per-row Edit and Delete buttons.
 * Uses createDataGrid with actions config and openDetailModal for editing.
 */
function renderCurrencyThresholds(container) {
  const { thresholds, aircraftTypes } = currencyThresholdsData;
  container.innerHTML = '';

  const grid = createDataGrid({
    columns: [
      { key: 'aircraft_type', label: 'Aircraft Type' },
      {
        key: 'min_hours_90_days',
        label: 'Min Hours (90 Days)',
        align: 'right',
        format: (val) => val != null ? String(val) : '—',
      },
    ],
    data: thresholds,
    defaultSort: 'aircraft_type',
    defaultOrder: 'asc',
    emptyMessage: 'No currency thresholds configured.',
    filterable: false,
    addLabel: 'Add Threshold',
    onAdd: () => openCurrencyThresholdModal(null, aircraftTypes),
    actions: [
      {
        label: 'Edit',
        className: 'data-grid__action-btn--edit',
        onClick: (row) => openCurrencyThresholdModal(row, aircraftTypes),
      },
      {
        label: 'Delete',
        className: 'data-grid__action-btn--delete',
        onClick: (row) => handleDeleteCurrencyThreshold(row),
      },
    ],
  });

  container.appendChild(grid);
}

/**
 * Open the detail modal for adding or editing a currency threshold.
 * @param {Object|null} threshold - Existing threshold for edit, null for add
 * @param {string[]} aircraftTypes - Available aircraft types
 */
function openCurrencyThresholdModal(threshold, aircraftTypes) {
  const isEdit = !!threshold;
  const title = isEdit
    ? `Edit Currency Threshold — ${threshold.aircraft_type}`
    : 'Add Currency Threshold';

  const typeOptions = aircraftTypes.map((t) => ({ value: t, label: t }));

  openDetailModal({
    title,
    fields: [
      {
        name: 'aircraft_type',
        label: 'Aircraft Type',
        type: typeOptions.length > 0 ? 'select' : 'text',
        required: true,
        ...(typeOptions.length > 0 ? { options: typeOptions } : {}),
      },
      { name: 'min_hours_90_days', label: 'Min Hours (90 Days)', type: 'number', required: true },
    ],
    values: isEdit
      ? {
          aircraft_type: threshold.aircraft_type,
          min_hours_90_days: threshold.min_hours_90_days,
        }
      : {},
    onSave: async (formData) => {
      const hours = parseFloat(formData.min_hours_90_days);
      const result = validateCurrencyThresholdPayload({
        aircraft_type: formData.aircraft_type?.trim(),
        min_hours_90_days: hours,
      });
      if (!result.valid) {
        const err = new Error(result.errors[0]);
        err.fieldErrors = {};
        result.errors.forEach((e) => {
          if (e.toLowerCase().includes('type')) err.fieldErrors.aircraft_type = e;
          else err.fieldErrors.min_hours_90_days = e;
        });
        throw err;
      }

      await upsertCurrencyThreshold({
        aircraft_type: formData.aircraft_type.trim(),
        min_hours_90_days: hours,
        updated_at: new Date().toISOString(),
      });

      showSettingsMsg(
        isEdit
          ? `Threshold for ${formData.aircraft_type} updated.`
          : `Threshold for ${formData.aircraft_type} added.`,
        false,
      );
      currencyThresholdsData.thresholds = await getCurrencyThresholds();
      const container = document.getElementById('currency-thresholds-section');
      if (container) renderCurrencyThresholds(container);
    },
  });
}

/**
 * Handle deletion of a currency threshold with confirmation and FK error handling.
 * @param {Object} threshold
 */
async function handleDeleteCurrencyThreshold(threshold) {
  const identifier = `currency threshold for ${threshold.aircraft_type}`;
  if (!confirmDelete(identifier)) return;

  try {
    await deleteCurrencyThreshold(threshold.id);
    showSettingsMsg(`Threshold for ${threshold.aircraft_type} deleted.`, false);
    currencyThresholdsData.thresholds = await getCurrencyThresholds();
    const container = document.getElementById('currency-thresholds-section');
    if (container) renderCurrencyThresholds(container);
  } catch (err) {
    if (isForeignKeyViolation(err)) {
      showSettingsMsg(`Cannot delete ${identifier}: it is referenced by other records.`, true);
    } else {
      showSettingsMsg(`Failed to delete threshold: ${err.message}`, true);
    }
  }
}


// ── Global Airports Management Section (Task 11.5) ───

/** In-memory cache for airports data. */
let airportsData = [];

/**
 * Load all airports and render the management section.
 */
async function loadAirportsManagement() {
  const container = document.getElementById('airports-management-section');
  if (!container) return;

  try {
    airportsData = await getAirports();
    renderAirportsManagement(container);
  } catch (err) {
    container.innerHTML = `<p class="error">Failed to load airports: ${err.message}</p>`;
  }
}

/**
 * Render the airports management section with Add, per-row Edit and Delete buttons.
 * Uses createDataGrid with actions config and openDetailModal for editing.
 */
function renderAirportsManagement(container) {
  container.innerHTML = '';

  const grid = createDataGrid({
    columns: [
      { key: 'icao_code', label: 'ICAO Code' },
      { key: 'name', label: 'Name' },
      { key: 'country_code', label: 'Country' },
      { key: 'airport_type', label: 'Type', format: (v) => v ? v.replace(/_/g, ' ') : '—' },
    ],
    data: airportsData,
    defaultSort: 'icao_code',
    defaultOrder: 'asc',
    emptyMessage: 'No airports in the global reference table.',
    filterable: true,
    addLabel: 'Add Airport',
    onAdd: () => openAirportModal(null),
    actions: [
      {
        label: 'Edit',
        className: 'data-grid__action-btn--edit',
        onClick: (row) => openAirportModal(row),
      },
      {
        label: 'Delete',
        className: 'data-grid__action-btn--delete',
        onClick: (row) => handleDeleteAirport(row),
      },
    ],
  });

  container.appendChild(grid);
}

/**
 * Open the detail modal for adding or editing an airport.
 * @param {Object|null} airport - Existing airport for edit, null for add
 */
function openAirportModal(airport) {
  const isEdit = !!airport;
  const title = isEdit ? `Edit Airport — ${airport.icao_code}` : 'Add Airport';

  openDetailModal({
    title,
    fields: [
      { name: 'icao_code', label: 'ICAO Code', type: 'text', required: true },
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'latitude', label: 'Latitude', type: 'number' },
      { name: 'longitude', label: 'Longitude', type: 'number' },
      { name: 'country_code', label: 'Country', type: 'text' },
      {
        name: 'airport_type',
        label: 'Type',
        type: 'select',
        options: [
          { value: 'large_airport', label: 'Large Airport' },
          { value: 'medium_airport', label: 'Medium Airport' },
          { value: 'small_airport', label: 'Small Airport' },
          { value: 'heliport', label: 'Heliport' },
          { value: 'seaplane_base', label: 'Seaplane Base' },
        ],
      },
    ],
    values: isEdit
      ? {
          icao_code: airport.icao_code,
          name: airport.name,
          latitude: airport.latitude,
          longitude: airport.longitude,
          country_code: airport.country_code,
          airport_type: airport.airport_type || 'small_airport',
        }
      : { airport_type: 'small_airport' },
    onSave: async (formData) => {
      const icao = formData.icao_code.trim().toUpperCase();
      const name = formData.name.trim();

      const icaoResult = validateICAOCode(icao);
      if (!icaoResult.valid) {
        const err = new Error(icaoResult.errors[0]);
        err.fieldErrors = { icao_code: icaoResult.errors[0] };
        throw err;
      }
      if (!name) {
        const err = new Error('Airport name is required.');
        err.fieldErrors = { name: 'Name is required' };
        throw err;
      }

      const payload = {
        icao_code: icao,
        name,
        airport_type: formData.airport_type,
      };
      if (formData.latitude) payload.latitude = parseFloat(formData.latitude);
      if (formData.longitude) payload.longitude = parseFloat(formData.longitude);
      if (formData.country_code) payload.country_code = formData.country_code.trim().toUpperCase();

      await upsertAirport(payload);
      showSettingsMsg(
        isEdit ? `Airport ${icao} updated successfully.` : `Airport ${icao} added successfully.`,
        false,
      );
      await loadAirportsManagement();
    },
  });
}

/**
 * Handle deletion of an airport with confirmation and FK error handling.
 * @param {Object} airport
 */
async function handleDeleteAirport(airport) {
  const identifier = airport.icao_code || airport.name;
  if (!confirmDelete(identifier)) return;

  try {
    await deleteAirport(airport.id);
    showSettingsMsg(`Airport ${identifier} deleted.`, false);
    await loadAirportsManagement();
  } catch (err) {
    if (isForeignKeyViolation(err)) {
      showSettingsMsg(`Cannot delete ${identifier}: it is referenced by other records (flights, bookings, etc.).`, true);
    } else {
      showSettingsMsg(`Failed to delete airport: ${err.message}`, true);
    }
  }
}

// ── Charges Section ───────────────────────────────────

/** In-memory cache for charges data. */
let chargesData = { charges: [], aircraft: [], chargeTypes: [] };

const PERIOD_RE = /^\d{4}(m(0[1-9]|1[0-2])|w(0[1-9]|[1-4][0-9]|5[0-3]))?(\/\d{4}(m(0[1-9]|1[0-2])|w(0[1-9]|[1-4][0-9]|5[0-3]))?)?$/;

async function loadCharges() {
  const container = document.getElementById('charges-section');
  if (!container) return;
  try {
    const [charges, aircraft, chargeTypes] = await Promise.all([
      getCharges(),
      getAircraft(),
      getChargeTypes(),
    ]);
    chargesData = { charges, aircraft, chargeTypes };
    renderCharges(container);
  } catch (err) {
    container.innerHTML = `<p class="error">Failed to load charges: ${err.message}</p>`;
  }
}

function renderCharges(container) {
  const { charges, aircraft, chargeTypes } = chargesData;
  container.innerHTML = '';

  const grid = createDataGrid({
    columns: [
      { key: 'charge_type', label: 'Type' },
      { key: 'period',      label: 'Period' },
      {
        key: 'aircraft_id',
        label: 'Aircraft',
        format: (val, row) => row.aircraft?.registration ?? '—',
      },
      { key: 'icao_code', label: 'Airport', format: (v) => v || '—' },
      { key: 'unit',      label: 'Unit' },
      { key: 'currency',  label: 'CCY' },
      { key: 'amount',    label: 'Amount', align: 'right', format: (v) => parseFloat(v).toFixed(4) },
      { key: 'notes',     label: 'Notes',  format: (v) => v || '—' },
    ],
    data: charges,
    defaultSort: 'period',
    defaultOrder: 'desc',
    emptyMessage: 'No charges configured.',
    filterable: true,
    addLabel: 'Add Charge',
    onAdd: () => openChargeModal(null, aircraft, chargeTypes),
    actions: [
      {
        label: 'Edit',
        className: 'data-grid__action-btn--edit',
        onClick: (row) => openChargeModal(row, aircraft, chargeTypes),
      },
      {
        label: 'Delete',
        className: 'data-grid__action-btn--delete',
        onClick: (row) => handleDeleteCharge(row),
      },
    ],
  });

  container.appendChild(grid);
}

function openChargeModal(charge, aircraft, chargeTypes) {
  const isEdit = !!charge;
  const title = isEdit ? `Edit Charge — ${charge.charge_type} ${charge.period}` : 'Add Charge';

  const aircraftOptions = [
    { value: '', label: '— Fleet-wide —' },
    ...aircraft.map((a) => ({ value: a.id, label: a.registration })),
  ];

  // Inject datalist for charge_type auto-suggest (needs to exist in the document)
  const datalistId = 'charge-type-suggestions';
  let datalist = document.getElementById(datalistId);
  if (!datalist) {
    datalist = document.createElement('datalist');
    datalist.id = datalistId;
    document.body.appendChild(datalist);
  }
  datalist.innerHTML = chargeTypes.map(t => `<option value="${t}">`).join('');

  openDetailModal({
    title,
    fields: [
      {
        name: 'charge_type',
        label: 'Charge Type',
        type: 'text',
        required: true,
        attrs: { list: datalistId, placeholder: 'e.g. FUEL, OIL, LANDING_FEE' },
      },
      {
        name: 'period',
        label: 'Period',
        type: 'text',
        required: true,
        attrs: { placeholder: 'e.g. 2026, 2025m12, 2026w03, 2025m01/2025m06' },
      },
      { name: 'aircraft_id', label: 'Aircraft (optional)', type: 'select', options: aircraftOptions },
      { name: 'icao_code',   label: 'Airport ICAO (optional)', type: 'text', attrs: { placeholder: 'e.g. EGLL' } },
      { name: 'unit',        label: 'Unit of Measure', type: 'text', required: true, attrs: { placeholder: 'e.g. per_litre, per_quart, per_landing' } },
      { name: 'currency',    label: 'Currency', type: 'text', required: true, attrs: { maxlength: 3, placeholder: 'GBP' } },
      { name: 'amount',      label: 'Amount', type: 'number', required: true, attrs: { step: '0.0001', min: '0' } },
      { name: 'notes',       label: 'Notes', type: 'text' },
    ],
    values: isEdit
      ? {
          charge_type: charge.charge_type,
          period:      charge.period,
          aircraft_id: charge.aircraft_id ?? '',
          icao_code:   charge.icao_code ?? '',
          unit:        charge.unit,
          currency:    charge.currency,
          amount:      charge.amount,
          notes:       charge.notes ?? '',
        }
      : { currency: 'GBP' },
    onSave: async (formData) => {
      const errors = {};

      if (!formData.charge_type?.trim()) errors.charge_type = 'Charge type is required.';
      if (!PERIOD_RE.test(formData.period?.trim() ?? ''))
        errors.period = 'Invalid period. Use YYYY, YYYYmNN, YYYYwNN or range e.g. 2025m01/2025m06.';
      if (!formData.unit?.trim()) errors.unit = 'Unit of measure is required.';
      if (!formData.currency?.trim() || formData.currency.trim().length !== 3)
        errors.currency = 'Currency must be a 3-letter code (e.g. GBP).';
      const amt = parseFloat(formData.amount);
      if (isNaN(amt) || amt < 0) errors.amount = 'Amount must be a non-negative number.';

      if (Object.keys(errors).length) {
        const err = new Error(Object.values(errors)[0]);
        err.fieldErrors = errors;
        throw err;
      }

      await upsertCharge({
        ...(isEdit ? { id: charge.id } : {}),
        charge_type: formData.charge_type.trim(),
        period:      formData.period.trim(),
        aircraft_id: formData.aircraft_id || null,
        icao_code:   formData.icao_code?.trim() || null,
        unit:        formData.unit.trim(),
        currency:    formData.currency.trim().toUpperCase(),
        amount:      amt,
        notes:       formData.notes?.trim() || null,
      });

      showSettingsMsg(isEdit ? 'Charge updated.' : 'Charge added.', false);
      const [charges, chargeTypes] = await Promise.all([getCharges(), getChargeTypes()]);
      chargesData.charges = charges;
      chargesData.chargeTypes = chargeTypes;
      const container = document.getElementById('charges-section');
      if (container) renderCharges(container);
    },
  });
}

async function handleDeleteCharge(charge) {
  const identifier = `${charge.charge_type} ${charge.period}`;
  if (!confirmDelete(identifier)) return;
  try {
    await deleteCharge(charge.id);
    showSettingsMsg(`Charge ${identifier} deleted.`, false);
    chargesData.charges = await getCharges();
    const container = document.getElementById('charges-section');
    if (container) renderCharges(container);
  } catch (err) {
    showSettingsMsg(`Failed to delete charge: ${err.message}`, true);
  }
}
